import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { exportICS, exportCSV, exportXLSX } from './icsexporter';
import './App.css';

function EventList({ events }) {
  const [visibleEvents, setVisibleEvents] = useState(20);

  return (
    <section className="mod_eventlist eventsArchive" aria-label="Liste der kommenden Feuerwehr-Events">
      {events.length === 0 ? (
        <p>Keine kommenden Events verfügbar</p>
      ) : (
        <>
          <ul>
            {events.slice(0, visibleEvents).map((event, index) => {
              const eventDt = DateTime.fromISO(event.date).setZone('Europe/Berlin');
              const now = DateTime.now().setZone('Europe/Berlin');
              const isToday = eventDt.hasSame(now, 'day');
              const isTomorrow = eventDt.hasSame(now.plus({ days: 1 }), 'day');
              const isNow = eventDt <= now.plus({ minutes: 30 }) && eventDt >= now;
              const isSoon = eventDt > now && eventDt <= now.plus({ minutes: 30 });

              const displayDate = isToday
                ? `Heute, ${event.formattedTime} Uhr`
                : isTomorrow
                ? `Morgen, ${event.formattedTime} Uhr`
                : `${event.formattedDate}, ${event.formattedTime} Uhr`;

              return (
                <li key={index}>
                  <blockquote
                    className={`event__item ${isToday ? 'today' : isTomorrow ? 'tomorrow' : ''} ${isNow ? 'now' : ''} ${isSoon ? 'soon' : ''} ${event.category === 'EA' ? 'einsatzabteilung' : ''}`}
                    itemScope
                    itemType="https://schema.org/Event"
                  >
                    <meta itemProp="startDate" content={eventDt.toISO()} />
                    <meta itemProp="name" content={event.title} />
                    <meta itemProp="eventStatus" content={`https://schema.org/Event${isToday || isTomorrow ? 'Scheduled' : 'Postponed'}`} />
                    <meta itemProp="eventAttendanceMode" content="https://schema.org/OfflineEventAttendanceMode" />
                    <div
                      className="event__item--date"
                      title={`${event.formattedDate}, ${event.formattedTime} Uhr`}
                      aria-label={`${event.formattedDate}, ${event.formattedTime} Uhr`}
                      itemProp="startDate"
                    >
                      {displayDate}
                    </div>
                    <div
                      className="event__item--title"
                      title={event.title}
                      aria-label={event.title}
                      itemProp="description"
                    >
                      {event.title}
                    </div>
                    <div className="event__item--category">
                      <span
                        className={`category__badge ${isToday ? 'today' : isTomorrow ? 'tomorrow' : ''}`}
                        title={`Kategorie: ${event.category}`}
                        aria-label={`Kategorie: ${event.category}`}
                        itemProp="organizer"
                      >
                        {event.category}
                      </span>
                    </div>
                  </blockquote>
                </li>
              );
            })}
          </ul>
          {visibleEvents < events.length && (
            <button
              className="load-more"
              onClick={() => setVisibleEvents(prev => prev + 10)}
              aria-label="Weitere Events anzeigen"
            >
              Mehr anzeigen
            </button>
          )}
        </>
      )}
    </section>
  );
}

function ExportModal({ onClose, events }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'auto'; };
  }, []);

  return (
    <div className="modal" role="dialog" aria-labelledby="export-modal-title">
      <h2 id="export-modal-title">Exportieren</h2>
      <button onClick={() => { exportICS(events); onClose(); }} aria-label="Events als ICS exportieren">ICS</button>
      <button onClick={() => { exportCSV(events); onClose(); }} aria-label="Events als CSV exportieren">CSV</button>
      <button onClick={() => { exportXLSX(events); onClose(); }} aria-label="Events als XLSX exportieren">XLSX</button>
      <button className="modal__cancel" onClick={onClose} aria-label="Export abbrechen">Abbrechen</button>
    </div>
  );
}

function App() {
  const [events, setEvents] = useState(() => {
    const savedEvents = localStorage.getItem('events');
    return savedEvents ? JSON.parse(savedEvents) : [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const connectWebSocket = () => {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
      const wsHost = window.location.hostname;
      const isDev = process.env.NODE_ENV === 'development' || wsHost === 'localhost';
      const wsPort = isDev ? '8085' : '';
      const wsUrl = isDev
        ? `${wsProtocol}${wsHost}:${wsPort}/ws`
        : `${wsProtocol}${wsHost}/ws`;

      console.log('Verbinde mit WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        const newEvents = JSON.parse(event.data);
        setEvents(newEvents);
        localStorage.setItem('events', JSON.stringify(newEvents)); // Speichere lokal
        setLoading(false);
      };
      ws.onerror = (err) => {
        console.error('WebSocket-Fehler:', err);
        setError('WebSocket-Fehler: Verbindung zum Server fehlgeschlagen');
      };
      ws.onclose = () => {
        console.log('WebSocket geschlossen, versuche Wiederverbindung...');
        setTimeout(connectWebSocket, 2000);
      };
      return ws;
    };
    const ws = connectWebSocket();
    return () => ws.close();
  }, []);

  if (loading && events.length === 0) return (
    <div className="loader" role="status" aria-live="polite">
      <span className="spinner"></span> Lade Events...
    </div>
  );
  if (error) return (
    <div className="error-modal" role="alert">
      <p>{error}</p>
      <button onClick={() => { setError(null); setLoading(true); }}>Erneut versuchen</button>
    </div>
  );

  return (
    <div className="app">
      <header>
        <h1 itemScope itemType="https://schema.org/Organization">
          <span itemProp="name">Abt. Schlichten - Kalender</span>
        </h1>
      </header>
      <main>
        <EventList events={events} />
        <button className="export-button" onClick={() => setShowModal(true)} aria-label="Events exportieren">
          Exportieren
        </button>
        {showModal && <ExportModal onClose={() => setShowModal(false)} events={events} />}
      </main>
    </div>
  );
}

export default App;